import { useMemo, useCallback } from "react";
import { Page, Text, View, Document, StyleSheet, Image, Font } from "@react-pdf/renderer";
import TableColPdf from "components/pdf/TableColPdf";
import BarcodePdf from "components/pdf/BarcodePdf";
import { QuoteDetail } from "features/quotation/types";
import sortQuoteDetailWheelsForDisplay from "features/quotation/utils/sortQuoteDetailWheelsForDisplay";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatWheelRimDiamDisplay,
  isPriceLabel,
} from "utils";
import { getTirePunctureDisplay } from "features/car_model/utils";
import TableWheelTireEstPreviewPdf from "./TableWheelTireEstPreviewPdf";

Font.register({
  family: "irohakakuC",
  src: "/fonts/irohakakuC-Regular.ttf",
});
Font.registerHyphenationCallback((word) => [word, ""]);

/**
 * Character-level wrap for CJK-containing tokens.
 * react-pdf splits words at ASCII spaces only, so long Japanese product names
 * ("コンパクトナット（クローム）ショートタイプ …") stay atomic and overflow the
 * neighboring column. Return each character as its own token to allow breaks anywhere.
 */
const cjkAwareHyphenation = (word: string): string[] => {
  if (/[぀-ゟ゠-ヿ一-鿿＀-￯]/.test(word)) {
    return word.split("");
  }
  return [word];
};

/** Render price with ¥ on the left and value centered in the cell for PDF. */
function renderPriceWithYen(value: string) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 2,
        paddingHorizontal: 4,
        width: "100%",
      }}
    >
      <Text style={{ fontSize: 7 }}>¥</Text>
      <Text style={{ fontSize: 7, flex: 1, textAlign: "center" }}>{value}</Text>
      {/* Empty view to balance the ¥ symbol for true center alignment */}
      <View style={{ width: 7 }} />
    </View>
  );
}

const DISABLED_TEXT = "なし" as const;
const ENABLED_TEXT = "する" as const;
const TAKE_OUT_DISABLED_TEXT = "しない" as const;

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "irohakakuC",
    fontSize: 12,
    color: "#000000",
  },
  header: { flexDirection: "row", marginBottom: 8 },
  headerInfo: { fontSize: 13, fontWeight: "bold" },
  headerLabel: { paddingLeft: 5, paddingRight: 5, minWidth: 50 },
  infoRow: {
    display: "flex",
    flexDirection: "row",
    gap: 2,
  },
  containerCarInfo: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1px solid #707070",
    paddingBottom: 12,
  },
  carInfo: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  containerDiscount: {
    display: "flex",
    gap: 1,
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 10,
    width: "100%",
  },
  carTitle: {
    fontSize: 14,
    fontWeight: "medium",
    textAlign: "center",
    color: "#333",
    marginBottom: 10,
  },
  containerQR: {
    display: "flex",
    flexDirection: "row",
  },
  specsCar: {
    display: "flex",
    flexDirection: "column",
    width: 120,
    marginTop: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginTop: 10,
  },
  footerQrRow: {
    flexDirection: "row",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  footerText: { fontSize: 10 },
  footerPageNumber: {
    fontSize: 10, textAlign: "center", width: "100%", position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0
  },
});

/**
 * PreviewWheelTirePdfEst component renders a PDF document for wheel tire quotation.
 * @param {object} props - Component props.
 * @param {QuoteDetail} props.quotationDetail - The quotation detail data.
 * @param {string} props.qrCodeData - Base64 string for the QR code image.
 */
export default function PreviewWheelTirePdfEst({
  quotationDetail,
  qrCodeData,
  optionsPrice,
  homeQrImageSrc,
}: {
  quotationDetail: QuoteDetail;
  qrCodeData: string;
  optionsPrice: number;
  homeQrImageSrc?: string;
}) {
  const hasTireData = useMemo<boolean>(
    () =>
      Boolean(
        quotationDetail.brnd_name &&
        quotationDetail.modl_code &&
        quotationDetail.modl_period_s &&
        quotationDetail.modl_period_e,
      ),
    [quotationDetail],
  );

  const sortedQuoteWheels = useMemo(
    () => sortQuoteDetailWheelsForDisplay(quotationDetail.tbl_quote_detail_wheel),
    [quotationDetail.tbl_quote_detail_wheel],
  );
  const renderCostValue = useCallback((cost: number | null, flag: number | null) => {
    if (flag === null || flag === 0) return "-";
    if (flag && (cost === 0 || cost === null)) return "無料";
    return formatCurrency(cost as number);
  }, []);

  // Discount row values for price columns (to support ¥ + value with space-between)
  const discReplac = formatCurrency(quotationDetail.replac_labor_cost);
  const discWaste = renderCostValue(
    quotationDetail.waste_tire_cost,
    quotationDetail.waste_tire_cost_flag,
  );
  const discRubber = renderCostValue(
    quotationDetail.rubber_stem_valve_cost,
    quotationDetail.rubber_stem_valve_flag,
  );
  const discNitrogen = renderCostValue(
    quotationDetail.nitrogen_inflation_cost,
    quotationDetail.nitrogen_inflation_flag,
  );
  const discHub = renderCostValue(
    quotationDetail.hub_rust_prevention_cost,
    quotationDetail.hub_rust_prevention_flag,
  );

  const tireSizeRender = useMemo(() => {
    if (!quotationDetail) return "";

    // For wheel type quotations, get tire size from the first block after display sort
    if (quotationDetail.quote_type === "wheel" && sortedQuoteWheels.length > 0) {
      const firstWheel = sortedQuoteWheels[0];
      const firstTire = firstWheel?.tbl_quote_detail_tire?.[0];

      if (firstTire) {
        const { midt_tire_width, midt_tire_aspect_ratio, midt_tire_rim_diam } = firstTire;
        if (midt_tire_width && midt_tire_aspect_ratio && midt_tire_rim_diam) {
          return `${midt_tire_width}/${midt_tire_aspect_ratio}R${midt_tire_rim_diam}`;
        }
      }
    }

    return "";
  }, [quotationDetail, sortedQuoteWheels]);

  /** ハブ径: API `mmmy_mmm_hub1` のみ（タイヤ rim へのフォールバックなし） */
  const hubRimDisplay = useMemo(
    () => formatWheelRimDiamDisplay(quotationDetail.mmmy_mmm_hub1),
    [quotationDetail.mmmy_mmm_hub1],
  );

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={{ ...styles.header, justifyContent: "space-between" }}>
          <View>
            <View style={{ display: "flex", flexDirection: "row" }}>
              <Text style={{ ...styles.headerInfo, ...styles.headerLabel }}>店舗名</Text>
              <Text style={styles.headerInfo}>：{quotationDetail?.store_name}</Text>
            </View>
            <View style={{ display: "flex", flexDirection: "row" }}>
              <Text style={{ ...styles.headerInfo, ...styles.headerLabel }}>住所</Text>
              <Text style={styles.headerInfo}>：{quotationDetail?.address}</Text>
            </View>
            <View style={{ display: "flex", flexDirection: "row" }}>
              <Text style={{ ...styles.headerInfo, ...styles.headerLabel }}>電話</Text>
              <Text style={styles.headerInfo}>
                ：{quotationDetail?.tel_no} &nbsp;&nbsp;&nbsp; 見積担当：
                {quotationDetail?.quote_by}
              </Text>
            </View>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <Text style={{ fontSize: 8 }}>
                当社グループは個人情報保護法に準拠し、適切な個人情報の取り扱いをしております。{"\n"}
                プライバシーポリシーは当社HPに記載及び店頭に掲示しております。
              </Text>
              <Image
                src={homeQrImageSrc || "/img/home_qr_code.png"}
                style={{ width: 45, height: 45 }}
              />
            </View>
          </View>
        </View>
        {/* car info */}
        <View style={{ ...styles.containerCarInfo, flexDirection: "column" }}>
          <View style={{ display: "flex", flexDirection: "row", marginTop: 14 }}>
            {/* Car information */}
            <View style={{ ...styles.carInfo, width: "100px", flexDirection: "column" }}>
              <Text style={styles.carTitle}>お見積書</Text>
              {quotationDetail.car_image_url && quotationDetail.modl_name && (
                <Image style={{ width: "75px" }} src={quotationDetail.car_image_url} />
              )}
            </View>
            {/* customer info */}
            <View style={{ display: "flex" }}>
              <View style={styles.infoRow}>
                <TableColPdf
                  styleBox={{ width: "120px" }}
                  title="見積番号"
                  content={quotationDetail.quote_no}
                />
                <TableColPdf
                  styleBox={{ width: "120px" }}
                  title="お客様氏名"
                  content={quotationDetail.customer_name}
                />
                <TableColPdf
                  styleBox={{ width: "120px" }}
                  title="作成日付"
                  content={formatDateTime(quotationDetail.created_at)}
                />
                <TableColPdf
                  styleBox={{ width: "120px" }}
                  title="有効期限"
                  content={formatDate(quotationDetail.exp_date)}
                />
              </View>
              <View style={{ ...styles.infoRow, marginTop: 4 }}>
                <TableColPdf
                  styleBox={{ width: "120px" }}
                  title="選択車両"
                  content={
                    hasTireData
                      ? `${quotationDetail.brnd_name} ${quotationDetail.modl_name} \n ${quotationDetail.modl_period_s}～${quotationDetail.modl_period_e}`
                      : ""
                  }
                />
                <TableColPdf
                  styleBox={{ width: "83px" }}
                  title="タイヤサイズ"
                  content={tireSizeRender}
                />
                <TableColPdf
                  styleBox={{ width: "35px" }}
                  title="ハブ径"
                  content={hubRimDisplay}
                />
              </View>
            </View>
          </View>
          {/* 税込表示（車両情報エリアの下辺・仕切り線の直上） */}
          <View style={{ alignItems: "flex-end", marginTop: 6 }}>
            <Text style={styles.footerText}>※金額は全て税込となります</Text>
          </View>
        </View>
        {/* discount */}
        <View style={styles.containerDiscount}>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: "#ace19d",
            }}
            title="交換工賃"
            content={!isPriceLabel(discReplac) ? discReplac : undefined}
          >
            {isPriceLabel(discReplac) ? renderPriceWithYen(discReplac) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.waste_tire_cost_flag ? "#dfdfdf" : "#ace19d",
            }}
            title="処分"
            content={!isPriceLabel(discWaste) ? discWaste : undefined}
          >
            {isPriceLabel(discWaste) ? renderPriceWithYen(discWaste) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.nitrogen_inflation_flag ? "#dfdfdf" : "#ace19d",
            }}
            title="窒素"
            content={!isPriceLabel(discNitrogen) ? discNitrogen : undefined}
          >
            {isPriceLabel(discNitrogen) ? renderPriceWithYen(discNitrogen) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.hub_rust_prevention_flag ? "#dfdfdf" : "#ace19d",
            }}
            title="ハブ防錆"
            content={!isPriceLabel(discHub) ? discHub : undefined}
          >
            {isPriceLabel(discHub) ? renderPriceWithYen(discHub) : null}
          </TableColPdf>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: "#dfdfdf",
            }}
            title="パンク補償"
            content={getTirePunctureDisplay(quotationDetail?.tire_puncture_flag)}
          />
        </View>
        <View style={styles.containerDiscount}>
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: quotationDetail.take_out_flag !== 1 ? "#dfdfdf" : "#ace19d",
            }}
            styleBox={{ width: "10%" }}
            title="車両への取付"
            content={quotationDetail.take_out_flag === 0 ? ENABLED_TEXT : TAKE_OUT_DISABLED_TEXT}
          />
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.nut_item_cd ? "#dfdfdf" : "#ace19d",
            }}
            styleBox={{ width: "15%" }}
            title="ナット"
            hyphenationCallback={cjkAwareHyphenation}
            content={
              quotationDetail.nut_item_cd
                ? `${quotationDetail.nut_option_name}\u00A0\u00A0${formatCurrency(quotationDetail.nut_sale_price || 0)}円`
                : DISABLED_TEXT
            }
          />
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.lock_nut_item_cd ? "#dfdfdf" : "#ace19d",
            }}
            styleBox={{ width: "15%" }}
            title="ロックナット"
            hyphenationCallback={cjkAwareHyphenation}
            content={
              quotationDetail.lock_nut_item_cd
                ? `${quotationDetail.locknut_option_name}\u00A0\u00A0${formatCurrency(quotationDetail.locknut_sale_price || 0)}円`
                : DISABLED_TEXT
            }
          />
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.hub_ring_item_cd ? "#dfdfdf" : "#ace19d",
            }}
            styleBox={{ width: "15%" }}
            title="ハブリング"
            hyphenationCallback={cjkAwareHyphenation}
            content={
              quotationDetail.hub_ring_item_cd
                ? `${quotationDetail.hubring_option_name}\u00A0\u00A0${formatCurrency(quotationDetail.hubring_sale_price || 0)}円`
                : DISABLED_TEXT
            }
          />
          <TableColPdf
            styleBoxTitle={{
              backgroundColor: !quotationDetail.wcoating_item_cd ? "#dfdfdf" : "#ace19d",
            }}
            styleBox={{ width: "15%" }}
            title="ホイールコーティング"
            hyphenationCallback={cjkAwareHyphenation}
            content={
              quotationDetail.wcoating_item_cd
                ? `${quotationDetail.wcoating_option_name}\u00A0\u00A0${formatCurrency(quotationDetail.wcoating_sale_price || 0)}円`
                : DISABLED_TEXT
            }
          />
        </View>
        <View style={{ ...styles.containerDiscount, marginTop: 2 }}>
          {[
            { key: "nut", janCode: undefined },
            { key: "locknut", janCode: quotationDetail.locknut_jan_cd },
            { key: "hubring", janCode: quotationDetail.hubring_jan_cd },
            { key: "wcoating", janCode: quotationDetail.wcoating_jan_cd },
          ].map(({ key, janCode }) => (
            <View
              key={`barcode-${key}`}
              style={{
                width: "15%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {janCode ? (
                <BarcodePdf value={janCode} />
              ) : (
                <View style={{ height: 20, width: "75%" }} />
              )}
            </View>
          ))}
        </View>

        {/* table est */}
        {sortedQuoteWheels.map((wheel) => (
          <View wrap={false} key={`wheel-${wheel.wheel_item_cd}-${wheel.pairing_number}`}>
            <TableWheelTireEstPreviewPdf
              optionsPrice={optionsPrice}
              wheelData={wheel}
              tireData={wheel.tbl_quote_detail_tire}
            />
          </View>
        ))}

        {/* Footer page number */}
        <Text
          style={styles.footerPageNumber}
          render={({ pageNumber, totalPages }) => totalPages > 1 ?
            `${pageNumber} / ${totalPages}` : ""
          }
          fixed
        />
      </Page>
    </Document>
  );
}
